import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Alert } from "react-native";
import { ApiResponse, isSuccessful } from "../services/api-requests";
import { profanity } from "@2toad/profanity";
import { notificationService } from "../services/notification-service";
import {
  getSingleChargerData,
  listMyChargers,
  getActiveOrdersByChargerId,
} from "../services/charger-service";
import {
  registerNewCharger,
  getResidentials,
  updateCharger,
  disableCharger,
  enableCharger,
} from "../store/actions/charging-actions";
import { getHostPlan, getUserInfo } from "../services/user-service";
import {
  generatePeakObj,
  setPeak,
  WeekConfiguration,
} from "../components/common/utils/peak-off-peak-field";
import { generateRegistrationPopup } from "../services/popup-service";
import {
  getCoordinatesFromGeodata,
  GetGoogleGeocode,
  getAddressComponentsFromGeodata,
} from "../components/common/utils/google-api-utils";
import { ChargerAttributes, createEmptyCharger } from "../models/charger-model";
import { AppDispatch } from "../store";
import { useDispatch } from "react-redux";
import { RouteProp, useNavigation, useRoute } from "@react-navigation/native";
import type { RootStackParamList } from "../navigation/stackTypes";
import {
  NativeStackNavigationProp,
  NativeStackScreenProps,
} from "@react-navigation/native-stack";
import { updateStripeSubscription } from "../services/stripe-service";
import { Prediction } from "../components/google-places-autocomplete";
import { Tooltip_t } from "../components/common/tooltips/tooltips";
import { userActions } from "../store/slices/user-slice";

type ScreenRouteProp = RouteProp<RootStackParamList, "RegisterUpdateCharger">;
type ScreenNavProp = NativeStackNavigationProp<
  RootStackParamList,
  "RegisterUpdateCharger"
>;

export const useChargerForm = () => {
  const route = useRoute<ScreenRouteProp>();
  const navigation = useNavigation<ScreenNavProp>();
  const editChargerStatus = Boolean(route.params?.editCharger);
  const [form, setForm] = useState<ChargerAttributes>(() =>
    createEmptyCharger()
  );
  const [errors, setErrors] = useState<any>({});
  const [isUpdating, setIsUpdating] = useState(false);
  const [isEnabled, setIsEnabled] = useState<boolean>(
    route.params?.chargerDetails?.is_enabled ?? true
  );
  const [verified, setVerified] = useState<ApiResponse<any> | null>(null);
  const [popupState, setPopupState] = useState<any>({
    visible: false,
    text: null,
    buttons: [],
  });
  const [residentialRatePeak, setResidentialRatePeak] = useState<number | null>(
    null
  );
  const [residentialRateOffPeak, setResidentialRateOffPeak] = useState<
    number | null
  >(null);
  const [rawPeak, setRawPeak] = useState<string>("");
  const [rawOffPeak, setRawOffPeak] = useState<string>("");
  const [peakSelection, setPeakSelection] = useState<WeekConfiguration>(() =>
    generatePeakObj("000000000000000000000000000000000000000000")
  );
  const dispatch: AppDispatch = useDispatch();

  useEffect(() => {
    async function getChargingDetails() {
      if (editChargerStatus && route.params.chargerDetails?.uuid) {
        const charger = await getSingleChargerData(
          route.params.chargerDetails.uuid
        );
        const { financials, is_enabled, lat, lng, peak_selection } = charger;
        setForm({
          ...charger,
        });
        setRawPeak(String(financials.price_kwh_peak));
        setRawOffPeak(String(financials.price_kwh_off_peak));
        setPeakSelection(generatePeakObj(peak_selection));
        setIsEnabled(is_enabled);
        // fetch residential rates for est
        if (lat && lng) {
          const estRates = await getResidentials(
            lat.toString(),
            lng.toString()
          );
          // Validate and convert the residential rate to a number
          const residentialRate = estRates?.outputs?.residential;
          const rateNumber = typeof residentialRate === 'number' ? residentialRate : parseFloat(residentialRate);
          const isValidRate = !isNaN(rateNumber) && isFinite(rateNumber);
          
          if (isValidRate) {
            setResidentialRateOffPeak(rateNumber);
            setResidentialRatePeak(rateNumber * 2);
          } else {
            setResidentialRateOffPeak(null);
            setResidentialRatePeak(null);
          }
        }
      } else {
        setForm((prev) => ({
          ...prev,
          unit_serial: route.params.unit_serial ?? "Error",
          name: route.params.name ?? "",
          charger_email: route.params.chargerEmail ?? "",
        }));
      }
    }
    getChargingDetails();
  }, []);

  const hidePopup = async () => {
    await updateStripeSubscription();
    setPopupState({ visible: false, text: null, buttons: [] });
  };

  const showPopup = (
    text: any,
    rawButtons: Array<{ text: string; onPress: () => void }>
  ) => {
    const wrapped = rawButtons.map((btn) => ({
      text: btn.text,
      onPress: async () => {
        hidePopup();
        btn.onPress();
      },
    }));
    setPopupState({ visible: true, text, buttons: wrapped });
  };

  // const toggleEnable = useCallback(async () => {
  //   if (!editChargerStatus) return;
  //   const uuid = route.params.chargerDetails.uuid;
  //   if (isEnabled) await disableCharger(uuid);
  //   else await enableCharger(uuid);
  //   setIsEnabled(!isEnabled);
  // }, []);
  const [isToggleLoading, setIsToggleLoading] = useState(false);
  const [showDisableWarning, setShowDisableWarning] = useState(false);

  const toggleEnable = useCallback(async () => {
    if (!editChargerStatus || isToggleLoading || !route.params.chargerDetails?.uuid) return;
    const uuid = route.params.chargerDetails.uuid;
    if (!uuid) return;
    
    // If charger is currently enabled (user wants to disable it), check for active orders
    if (isEnabled) {
      setIsToggleLoading(true);
      try {
        const activeOrdersResponse = await getActiveOrdersByChargerId(uuid);
        
        // First check if the API call was successful
        if (isSuccessful(activeOrdersResponse.status) && activeOrdersResponse.payload) {
           // Check for active orders - API returns 'orders' (plural)
           if (activeOrdersResponse.payload.orders && activeOrdersResponse.payload.orders.length > 0) {
             setIsToggleLoading(false);
             setShowDisableWarning(true);
             return; // Important: stop execution here to show the popup
           } else {
             // No active orders, proceed with disable
             setIsToggleLoading(false);
             await performToggle();
             return;
           }
         } else {
           notificationService.sendNotification('error', 'Unable to disable charger');
           setIsToggleLoading(false);
           return;
         }
      } catch (error) {
        console.error("Error checking active orders:", error);
        setIsToggleLoading(false);
        return;
      }
    }

    // Proceed with the toggle (either enabling or disabling without active orders)
    await performToggle();
  }, [editChargerStatus, isToggleLoading, isEnabled, route.params.chargerDetails?.uuid]);

  const performToggle = useCallback(async () => {
    if (!route.params.chargerDetails?.uuid) return;
    const uuid = route.params.chargerDetails.uuid;
    if (!uuid) return;
    setIsToggleLoading(true);

    try {
      // Get current state using functional update
      setIsEnabled(currentIsEnabled => {
        // Make the API call based on current state
        const apiCall = currentIsEnabled ? disableCharger(uuid) : enableCharger(uuid);
        
        apiCall.then(result => {
          if (result) {
            setIsEnabled(!currentIsEnabled);
          }
        }).catch(error => {
          console.error("Error toggling charger status:", error);
        }).finally(() => {
          setIsToggleLoading(false);
        });
        
        return currentIsEnabled; // Return current value to avoid immediate state change
      });
    } catch (error) {
      console.error("Error toggling charger status:", error);
      setIsToggleLoading(false);
    }
  }, [route.params.chargerDetails?.uuid]);

  const handleDisableConfirm = useCallback(async () => {
    // Set flag to suppress automatic ChargeSummary navigation
    dispatch(userActions.setSuppressChargeSummaryNavigation(true));
    
    setShowDisableWarning(false);
    await performToggle();
    
    // Clear the suppression flag after a short delay to allow for any pending navigation
    setTimeout(() => {
      dispatch(userActions.setSuppressChargeSummaryNavigation(false));
    }, 5000); // 5 second delay
  }, [performToggle, dispatch]);

  const handleDisableCancel = useCallback(() => {
    setShowDisableWarning(false);
  }, []);

  const handleNameChange = useCallback(
    (text: string) => {
      if (profanity.exists(text)) {
        setErrors((prev: any) => ({
          ...prev,
          name: "Please, no profanity in the name",
        }));
      } else if (text.length > 50) {
        setErrors((prev: any) => ({
          ...prev,
          name: "Name cannot exceed 50 characters",
        }));
      } else {
        setErrors((prev: { [x: string]: any; name: any }) => {
          const { name, ...rest } = prev;
          return rest;
        });
      }
      setForm((prev) => ({ ...prev, name: text }));
    },
    [errors, form]
  );

  const handleDescriptionChange = useCallback(
    (text: string) => {
      if (profanity.exists(text)) {
        setErrors((prev: any) => ({
          ...prev,
          description: "Please, no profanity in the description",
        }));
      } else {
        setErrors((prev: { [x: string]: any; description: any }) => {
          const { description, ...rest } = prev;
          return rest;
        });
      }
      setForm((prev) => ({ ...prev, description: text }));
    },
    [errors, form]
  );

  const handleChargerEmailChange = useCallback(
    (text: string) => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (profanity.exists(text)) {
        setErrors((prev: any) => ({
          ...prev,
          charger_email: "Please, no profanity in the email",
        }));
      } else if (!emailRegex.test(text)) {
        setErrors((prev: any) => ({
          ...prev,
          charger_email: "Please enter a valid email address",
        }));
      } else {
        setErrors((prev: { [x: string]: any; charger_email: any }) => {
          const { charger_email, ...rest } = prev;
          return rest;
        });
      }
      setForm((prev) => ({ ...prev, charger_email: text }));
    },
    [errors, form]
  );

  const handleAddressSelect = useCallback(
    async (data: Prediction) => {
      try {
        // Get geocode data
        const geo = await GetGoogleGeocode(data.description);
        
        // Validate geocode response
        if (!geo) {
          notificationService.sendNotification('error', 'Unable to process the selected address. Please try a different address.');
          return;
        }

        // Extract coordinates
        const { latitude, longitude } = getCoordinatesFromGeodata(geo);
        
        // Validate coordinates
        if (latitude === 0 && longitude === 0) {
          notificationService.sendNotification('error', 'Unable to get valid coordinates for this address. Please try a different address.');
          return;
        }

        // Extract address components
        const address = getAddressComponentsFromGeodata(geo);
        
        // Validate that we have at least country and some address info
        if (!address.country) {
          notificationService.sendNotification('warning', 'Address may be incomplete. Please verify all fields.');
        }

        // Update form with all extracted data
        setForm((prev) => ({
          ...prev,
          full_address: data.description,
          lat: latitude,
          lng: longitude,
          city: address.city || '',
          state: address.state || '',
          country: address.country || '',
          street_name: address.street_name || '',
          street_number: address.street_number || '',
          zip_code: address.zip_code || '',
        }));

        // Clear any previous address errors
        setErrors((prev: { [x: string]: any; full_address: any }) => {
          const { full_address, ...rest } = prev;
          return rest;
        });

        // Fetch residential rates (may not be available for all locations)
        try {
          const estRates = await getResidentials(
            latitude.toString(),
            longitude.toString()
          );
          
          const residentialRate = estRates?.outputs?.residential;
          const rateNumber = typeof residentialRate === 'number' ? residentialRate : parseFloat(residentialRate);
          const isValidRate = !isNaN(rateNumber) && isFinite(rateNumber);
          
          if (isValidRate) {
            setResidentialRateOffPeak(rateNumber);
            setResidentialRatePeak(rateNumber * 2);
          } else {
            setResidentialRateOffPeak(null);
            setResidentialRatePeak(null);
          }
        } catch (rateError) {
          // Don't block address selection if rates fail
          setResidentialRateOffPeak(null);
          setResidentialRatePeak(null);
        }
      } catch (error) {
        console.error('Error in handleAddressSelect:', error);
        notificationService.sendNotification('error', 'An error occurred while processing the address. Please try again.');
      }
    },
    [form]
  );

  const handleKwhChange = useCallback(
    (key: "price_kwh_peak" | "price_kwh_off_peak", text: string) => {
      const regex = /^[0-9]*\.?[0-9]*$/; // allow leading zero or decimal
      if (!regex.test(text)) return; // ignore invalid characters

      // Update the appropriate raw state so the TextInput shows exactly what they type
      if (key === "price_kwh_peak") setRawPeak(text);
      else setRawOffPeak(text);

      // Then parse and store numeric value in the form
      const numeric = parseFloat(text);
      setForm((prev) => ({
        ...prev,
        financials: {
          ...prev.financials,
          [key]: isNaN(numeric) ? 0 : parseFloat(numeric.toFixed(2)),
        },
      }));
    },
    [form]
  );

  const handlePeakChange = useCallback(
    (peakSel: WeekConfiguration) => {
      setPeakSelection(peakSel);
      setForm((prev) => ({
        ...prev,
        peak_selection: setPeak(peakSel),
      }));
    },
    [form]
  );

  const submitCreate = async () => {
    if (!form.full_address)
      return setErrors((prev: any) => ({
        ...prev,
        full_address: "Please select a valid address",
      }));
    if (!form.name)
      return setErrors((prev: any) => ({
        ...prev,
        name: "Please enter a charger name",
      }));

    setIsUpdating(true);
    const { financials, uuid, ...restOfForm } = form;

    let cost_est_kwh_peak_update = 0;
    let cost_est_kwh_off_peak_update = 0;
    if (residentialRatePeak !== null && residentialRateOffPeak !== null) {
      cost_est_kwh_peak_update = residentialRatePeak;
      cost_est_kwh_off_peak_update = residentialRateOffPeak;
    } else {
      cost_est_kwh_peak_update = 0;
      cost_est_kwh_off_peak_update = 0;
    }

    const payload = {
      ...restOfForm,
      price_kwh_peak: rawPeak,
      price_kwh_off_peak: rawOffPeak,
      cost_kwh_peak: cost_est_kwh_peak_update,
      cost_kwh_off_peak: cost_est_kwh_off_peak_update,
      manufacturer_name: route.params.manufacturer,
      charger_model: "Emporia X1",
      charger_type: "Level 2",
      charging_capacity: 40,
    };

    const resp = await registerNewCharger(payload);

    if (!isSuccessful(resp.status)) {
      const errorMessage =
        resp?.payload?.message ?? "Could not register this charger";
      // Check if the error message indicates an invalid manufacturer_device_id
      if (errorMessage.includes("not found")) {
        // Set the charger email error message so the user knows to correct the field
        setErrors((prev: any) => ({
          ...prev,
          charger_email:
            "Invalid charger email. Please enter a valid email associated with your charger manufacturer.",
        }));
        //scrollToEmailField();
      }
      notificationService.sendNotification("error", errorMessage);
      setIsUpdating(false);
      return;
    }

    const userInfo = await getUserInfo();
    const myChargers = await listMyChargers();
    if (!myChargers || !isSuccessful(myChargers.status)) {
      notificationService.sendNotification(
        "error",
        "Error retrieving your chargers"
      );
    }
    const plan = await getHostPlan();
    const { text, buttons } = generateRegistrationPopup(
      userInfo,
      myChargers?.payload?.length ?? 0,
      plan?.hostPlan?.price ?? 0,
      hidePopup,
      navigation
    );
    setPopupState({ visible: true, text, buttons });
    setIsUpdating(false);
  };

  const submitUpdate = async () => {
    setIsUpdating(true);

    let cost_est_kwh_peak_update = 0;
    let cost_est_kwh_off_peak_update = 0;

    if (residentialRatePeak !== null && residentialRateOffPeak !== null) {
      cost_est_kwh_peak_update = residentialRatePeak;
      cost_est_kwh_off_peak_update = residentialRateOffPeak;
    } else {
      cost_est_kwh_peak_update = 0;
      cost_est_kwh_off_peak_update = 0;
    }

    const { financials, uuid, ...restOfForm } = form;

    const payload = {
      ...restOfForm,
      price_kwh_peak: rawPeak,
      price_kwh_off_peak: rawOffPeak,
      cost_kwh_peak: cost_est_kwh_peak_update,
      cost_kwh_off_peak: cost_est_kwh_off_peak_update,
    };

    dispatch(updateCharger(payload, route.params.chargerDetails?.uuid ?? ''));
    navigation.navigate('MyChargers' as any);
  };

  const onPopupClose = useCallback(async () => {
    setPopupState({ visible: false, text: null, buttons: [] });
    await updateStripeSubscription();
  }, []);

  const handlers = useMemo(
    () => ({
      onNameChange: handleNameChange,
      onDescriptionChange: handleDescriptionChange,
      onChargerEmailChange: handleChargerEmailChange,
      onKwhChange: handleKwhChange,
      onPeakChange: handlePeakChange,
      onAddressSelect: handleAddressSelect,
      toggleEnable,
      onPopupClose,
    }),
    [form]
  );

  return {
    form,
    errors,
    isUpdating,
    isEnabled,
    isToggleLoading,
    editChargerStatus,
    popupState,
    verified,
    setVerified,
    handlers,
    submitCreate,
    submitUpdate,
    residentialRatePeak,
    residentialRateOffPeak,
    rawPeak,
    rawOffPeak,
    peakSelection,
    showDisableWarning,
    handleDisableConfirm,
    handleDisableCancel,
    disableWarningData: {
      header: "Warning",
      text: "There are active or scheduled charges on your charger. By pausing services on this charger, all active charges will end and you can control your charger through its local App. \n\nAre you sure you want to continue?"
    } as Tooltip_t,
  };
};
